/**
 * ProjectErrorBoundary — a hard safety net around the project work surface.
 *
 * RECURRING FAILURE MODE (owner-locked memory): a single child component that
 * throws during render — with NO error boundary — silently unmounts the whole
 * subtree, leaving a page that "renders but nothing is clickable" (the dead
 * page). Text-assert tests never catch it. This boundary CATCHES any such throw,
 * keeps the rest of the app interactive, and — critically — SHOWS the actual
 * error + component stack ON SCREEN so the failure is self-reporting instead of
 * silent. It also logs to the console (and a global hook) for capture.
 *
 * One isolated `componentStack` lets staff/owner read exactly which component
 * broke and relay it, instead of staring at a dead page.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** human label for which surface this guards (shown in the fallback). */
  surface?: string;
}
interface State {
  error: Error | null;
  componentStack: string | null;
}

export default class ProjectErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Loud console error (captured by read_console_messages / DevTools) +
    // a structured global so any monitor can pick it up.
    // eslint-disable-next-line no-console
    console.error(
      `[ProjectErrorBoundary] ${this.props.surface || 'project surface'} crashed:`,
      error,
      info.componentStack,
    );
    this.setState({ componentStack: info.componentStack ?? null });
    try {
      (window as unknown as { __AVBP_LAST_ERROR__?: unknown }).__AVBP_LAST_ERROR__ = {
        surface: this.props.surface || 'project surface',
        message: error?.message,
        stack: error?.stack,
        componentStack: info.componentStack,
        at: new Date().toISOString(),
      };
    } catch {
      /* never throw from the boundary */
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div
          data-testid="project-error-boundary"
          role="alert"
          style={{
            border: '1.5px solid var(--crit, #b85c4a)',
            background: 'var(--crit-soft, #f5e3de)',
            borderRadius: 8,
            padding: '14px 16px',
            margin: '12px 0',
            fontSize: 13,
            color: 'var(--ink, #2a2722)',
            lineHeight: 1.55,
          }}
        >
          <strong style={{ fontSize: 14 }}>
            This section hit an error and was paused (the rest of the page still works).
          </strong>
          <p style={{ margin: '8px 0 0' }}>
            {this.props.surface ? `Surface: ${this.props.surface}. ` : ''}
            Please screenshot this and send it over — it names exactly what broke.
          </p>
          <pre
            style={{
              marginTop: 10,
              padding: '10px 12px',
              background: '#fff',
              border: '1px solid var(--rule, #d8d4c8)',
              borderRadius: 6,
              fontSize: 11.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 240,
              overflow: 'auto',
            }}
          >
            {String(this.state.error?.message || this.state.error)}
            {this.state.componentStack ? `\n${this.state.componentStack}` : ''}
          </pre>
          <button
            type="button"
            onClick={() => this.setState({ error: null, componentStack: null })}
            style={{
              marginTop: 10,
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid var(--rule, #d8d4c8)',
              background: '#fff',
              cursor: 'pointer',
              fontSize: 12.5,
              fontFamily: 'inherit',
            }}
          >
            Retry this section
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
